import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import without from 'lodash/without';
import startsWith from 'lodash/startsWith';
import { connect } from 'react-redux';
import Cookie from 'js-cookie';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { isUserAdmin } from '../Authorization';
import Icon from '../../components/Icon';
import Confirm from '../../components/Confirm';
import Popup from '../../components/Popup';
import Form from '../../components/Form';
import Ref from '../../components/Ref';
import Dropdown from '../../components/Dropdown';
import Input from '../../components/Input';
import Button from '../../components/Button';
import HelpLink from '../HelpLink';
import { signOut } from '../Authorization/actions';
import { reload } from '../../utils';

import './styles.css';

const messages = defineMessages({
  userDropdown: {
    id: 'user.dropdown',
    defaultMessage: 'User Dropdown',
  },
  isAdmin: {
    id: 'user.dropdown.isAdmin',
    defaultMessage: 'ADMIN',
  },
  signout: {
    id: 'user.dropdown.signout',
    defaultMessage: 'Sign Out',
  },
  yourProfile: {
    id: 'user.dropdown.yourProfile',
    defaultMessage: 'Your Profile',
  },
  updateProfile: {
    id: 'user.dropdown.updateProfile',
    defaultMessage: 'Update Profile',
  },
  institution: {
    id: 'user.dropdown.institution',
    defaultMessage: 'Institution Profile',
  },
  accountSettings: {
    id: 'user.dropdown.accountSettings',
    defaultMessage: 'Account Settings',
  },
  loggedInAs: {
    id: 'user.dropdown.loggedInAs',
    defaultMessage: 'Logged in as {currentUser}',
  },
  support: {
    id: 'help.support',
    defaultMessage: 'Contact Support',
  },
});

export const MenuItemAccountSettings = ({ intl, onClick }) => (
  <Dropdown.Item
    key="user-dropdown-change-profile"
    icon="setting"
    value="accountSettings"
    text={intl.formatMessage(messages.accountSettings)}
    onClick={onClick}
  />
);

MenuItemAccountSettings.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const MenuItemInstutitionProfile = ({ intl, onClick }) => (
  <Dropdown.Item
    key="user-dropdown-institution"
    data-cy="user-droppdown-institution"
    icon="users"
    value="institution"
    text={intl.formatMessage(messages.institution)}
    onClick={onClick}
  />
);
MenuItemInstutitionProfile.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const MenuItemYourProfile = ({ intl, onClick }) => (
  <Dropdown.Item
    onClick={onClick}
    key="user-dropdown-update-profile"
    data-cy="user-droppdown-update-profile"
    icon="user"
    value="updateProfile"
    text={intl.formatMessage(messages.yourProfile)}
  />
);
MenuItemYourProfile.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const getUserNameFromViewer = (viewer) => {
  let userName = get(viewer, 'idToken.name');
  if (userName === undefined) {
    const givenName = get(viewer, 'idToken.given_name');
    const familyName = get(viewer, 'idToken.family_name');
    userName = (givenName !== undefined) ? givenName : '';
    if (userName.length > 0 && familyName !== undefined && familyName.length > 0) {
      userName += ' ';
    }
    userName += (familyName !== undefined) ? familyName : '';
  }
  return userName;
};
const MenuItemLoggedInAs = ({ onClick, user }) => (
  <Dropdown.Item
    icon={null}
    disabled
    key="user-dropdown-loggedInAs"
    value="loggedInAs"
    onClick={onClick}
  >
    <span>
      <FormattedMessage {...messages.loggedInAs} values={{ currentUser: getUserNameFromViewer(user) }} />
      { isUserAdmin(user) &&
        <span styleName="user-dropdown-is-admin"><FormattedMessage {...messages.isAdmin} /></span>}
    </span>
  </Dropdown.Item>
);
MenuItemLoggedInAs.propTypes = {
  onClick: PropTypes.func.isRequired,
  user: PropTypes.object,
};

class UserInfo extends React.PureComponent {
  static propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.object.isRequired,
    }).isRequired,
    signOut: PropTypes.func.isRequired,
    mutateAddFeatureFlag: PropTypes.func.isRequired,
    mutateRemoveFeatureFlag: PropTypes.func.isRequired,
    viewer: PropTypes.shape({
      enabledFeatures: PropTypes.array.isRequired,
      globallyEnabledFeatures: PropTypes.array.isRequired,
    }).isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    isError: false,
    requesting: false,
  };

  onChange = (e, { value }) => {
    const { history, viewer, current } = this.props;
    const isCommunityServerlessOn = get(viewer, 'enabledFeatures', []).includes('COMMUNITYSERVERLESS');
    const profileUrl = isCommunityServerlessOn ? `/counselorcommunity/search/people/${get(viewer, 'uuid', '')}` : '/community/profile';
    const institutionUrl = isCommunityServerlessOn ? `/counselorcommunity/search/institutions/highschools/${get(viewer, 'hsInstitutionId', '')}` : '/community/institution';

    if (value === 'signout') {
      // TODO: Remove this when sync with the backend
      const googleAuthInstance = window.gapi ? window.gapi.auth2.getAuthInstance() : null;
      if (googleAuthInstance && googleAuthInstance.isSignedIn.get()) {
        // disconnect google calendar
        googleAuthInstance.disconnect();
        googleAuthInstance.signOut();
      }
      this.props.signOut(this.props.current.viewer);
    } else if (value === 'updateProfile') {
      history.push({
        pathname: profileUrl,
        state: { lastUrlBeforeVisited: window.location.pathname },
      });

      window.dispatchEvent(new Event('counselor-community-serverless-update-route'));
    } else if (value === 'institution') {
      history.push({
        pathname: institutionUrl,
        state: { lastUrlBeforeVisited: window.location.pathname },
      });

      window.dispatchEvent(new Event('counselor-community-serverless-update-route'));
    } else if (value === 'accountSettings') {
      if (!current.viewer.isNaviance) {
        history.push('/settings/change-profile');
      } else {
        history.push('/settings/your-notifications');
      }
    } else if (value === 'addFeatureFlag' || value === 'globalFeatures') {
      e.stopPropagation();
    } else if (typeof value === 'string' && startsWith(value, 'feature:')) {
      const feature = value.substring('feature:'.length);
      if (!viewer.globallyEnabledFeatures.includes(feature)) {
        this.setState({ removeFeatureFlag: feature });
      }
      e.stopPropagation();
    }
  };

  onAddFeatureKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.addFeatureFlag(e.target);
      e.target.value = '';
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  onAddFeatureSubmit = (e) => {
    this.addFeatureFlag(e.target.elements[0]);
    e.target.elements[0].value = '';
  };

  onFailure = (transaction) => {
    this.setState({ isError: true, requesting: false });
  };

  onSuccess = () => {
    this.setState({ isError: false, requesting: false });
  };

  switchBranch = () => {
    if (Cookie.get('x-source')) {
      Cookie.remove('x-source');
    } else {
      Cookie.set('x-source', 'release');
    }
    reload();
  };

  getSourceCookieDropdownItem(sourceCookie) {
    return (
      <Dropdown.Item value="branch" key="branch" onClick={this.switchBranch}>
        <Icon name="code branch" />
        {sourceCookie || process.env.BUILDBRANCH}
      </Dropdown.Item>
    );
  }

  getFeatureToggleDropdownItems() {
    const { viewer: { enabledFeatures, globallyEnabledFeatures } } = this.props;
    const { isError, requesting } = this.state;
    return [
      <Dropdown.Item value="addFeatureFlag" key="addFeatureFlag" onClick={this.onChange}>
        <Form onSubmit={this.onAddFeatureSubmit}>
          <Input action error={isError}>
            <input data-cy="feature-flag-input" placeholder="Add Feature Flag" aria-label="Add Feature Flag" onKeyDown={this.onAddFeatureKeyPress} />
            <Button data-cy="feature-flag-button" loading={requesting} aria-label="Add Feature Flag" color="teal" type="submit" icon><Icon name="plus" /></Button>
          </Input>
        </Form>
      </Dropdown.Item>,
      <Dropdown.Item value="globalFeatures" key="globalFeatures" onClick={this.onChange}>
        <Icon name="globe" />
        <Popup trigger={<span styleName="global-features-overflow">{globallyEnabledFeatures.join(', ')}</span>}>
          {globallyEnabledFeatures.map((feature) => (<div key={feature}>{feature}</div>))}
        </Popup>
      </Dropdown.Item>,
      ...without(enabledFeatures, ...globallyEnabledFeatures).map((feature) => {
        const featureKey = `feature:${feature}`;
        return <Dropdown.Item value={featureKey} key={featureKey} text={feature} icon="flag" onClick={this.onChange} />;
      }),
    ];
  }

  addFeatureFlag(e) {
    if (!e.value) {
      return;
    }
    this.setState({ isError: false, requesting: true });
    const { onFailure, onSuccess } = this;
    this.props.mutateAddFeatureFlag({
      variables: {
        input: {
          featureFlag: e.value,
        },
      },
    }).then(onSuccess, onFailure);
  }

  removeFeatureFlag(featureFlag) {
    this.setState({ isError: false, requesting: true });
    const { onFailure, onSuccess } = this;
    this.props.mutateRemoveFeatureFlag({
      variables: {
        input: {
          featureFlag,
        },
      },
    }).then(onSuccess, onFailure);
  }

  handleRemoveCancel = () => {
    this.setState({ removeFeatureFlag: '' });
    if (this.dropdownRef) {
      this.dropdownRef.focus();
    }
  };

  handleRemoveConfirm = () => {
    const { removeFeatureFlag } = this.state;
    this.removeFeatureFlag(removeFeatureFlag);
    this.setState({ removeFeatureFlag: '' });
    if (this.dropdownRef) {
      this.dropdownRef.focus();
    }
  };

  handleRef = (ref) => {
    this.dropdownRef = ref;
  };

  isCommunityAdmin = (current) => {
    const data = get(current, 'viewer.idToken', {});
    const getNavianceClaim = (token, claim) => get(token, `https://oauth.naviance.com/oauth/claims/${claim}`, '');
    return getNavianceClaim(data, 'community.admin') === true;
  }

  render() {
    const { viewer, current, intl } = this.props;
    const { removeFeatureFlag } = this.state;
    const isImpersonating = get(current, 'viewer.idToken.impersonator');
    const isNaviance = get(current, 'viewer.isNaviance');
    const isCommunityAdmin = this.isCommunityAdmin(current);
    const sourceCookie = Cookie.get('x-source');
    const userAlert = sourceCookie ?
      (<Icon name="code branch" circular inverted color="pink" size="small" styleName="alertBubble" />) : null;
    const trigger = <Icon name="user circle" size="big" styleName="avatarNav" />;
    return (
      <span>
        { removeFeatureFlag && (
        <Confirm
          open
          header="Remove Feature Flag?"
          content={`Are you sure you want to remove the ${removeFeatureFlag} feature flag?`}
          onCancel={this.handleRemoveCancel}
          onConfirm={this.handleRemoveConfirm}
        />
        )}
        <Ref innerRef={this.handleRef}>
          <Dropdown
            aria-label={intl.formatMessage(messages.userDropdown)}
            selectOnBlur={false}
            selectOnNavigation={false}
            styleName="userDropdown white"
            scrolling
            id="user-dropdown"
            className="top right"
            onChange={this.onChange}
            floating
            icon={userAlert}
            trigger={trigger}
          >
            <Dropdown.Menu>
              {(viewer.enabledFeatures.includes('BRANCH_TOGGLER') || sourceCookie) && this.getSourceCookieDropdownItem(sourceCookie)}
              {!isNaviance && !isImpersonating && (
              <MenuItemAccountSettings
                intl={intl}
                onClick={this.onChange}
              />
              )}
              {!isNaviance && !isImpersonating &&
                <Dropdown.Divider />}
              {!isImpersonating &&
                <MenuItemYourProfile intl={intl} onClick={this.onChange} />}
              {(() => {
                if (isImpersonating) {
                  return null;
                } else if (isNaviance && !isCommunityAdmin) {
                  return null;
                } else {
                  return (
                    <MenuItemInstutitionProfile intl={intl} onClick={this.onChange} />
                  );
                }
              })()}
              {!isNaviance &&
                <Dropdown.Divider className="responsive-display" styleName="responsive-display" />}
              {!isNaviance &&
                <HelpLink idPrefix="helpUserMenu" parentMenu="userInfo" messageId="app.secondaryMenu.help" defaultMessage="Help" />}
              {!isNaviance && (
                <a
                  className="item"
                  styleName="responsive-display"
                  data-cy="contact-support-email"
                  href={`mailto:${process.env.COUNSELOR_COMMUNITY_EMAIL}`}
                >
                  { intl.formatMessage(messages.support) }
                </a>
              )}

              <Dropdown.Divider />
              <MenuItemLoggedInAs onClick={this.onChange} user={current.viewer} />

              <Dropdown.Item
                onClick={this.onChange}
                icon="sign out"
                key="user-dropdown-signout"
                value="signout"
                text={intl.formatMessage(messages.signout)}
              />
              {viewer.enabledFeatures.includes('ENABLE_FEATURE_TOGGLER') && this.getFeatureToggleDropdownItems()}
            </Dropdown.Menu>
          </Dropdown>
        </Ref>
      </span>
    );
  }
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export const UserInfoComponent = injectIntl(UserInfo);

export default withRouter(connect(mapStateToProps, { signOut })(UserInfoComponent));
